"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useToast } from "@/hooks/use-toast"
import { API_BASE_URL } from "@/lib/api"

export function CreateAdmin() {
  const [form, setForm] = useState({
    userId: "",
    name: "",
    pin: "",
    confirmPin: ""
  })
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (form.pin !== form.confirmPin) {
      toast({
        title: "Error",
        description: "PINs do not match",
        variant: "destructive",
      })
      return
    }

    if (form.pin.length < 4) {
      toast({
        title: "Error",
        description: "PIN must be at least 4 digits",
        variant: "destructive",
      })
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/admin/create-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: form.userId,
          name: form.name,
          pin: form.pin
        }),
      })

      const data = await response.json()

      if (response.ok) {
        toast({
          title: "Success",
          description: "Admin created successfully",
          variant: "default",
        })
        setForm({ userId: "", name: "", pin: "", confirmPin: "" })
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to create admin",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error creating admin:', error)
      toast({
        title: "Error",
        description: "Failed to create admin",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Create Admin Account</CardTitle>
        <CardDescription>
          Create a new admin account for the system
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="userId">Admin User ID</Label>
            <Input
              id="userId"
              value={form.userId}
              onChange={(e) => setForm({...form, userId: e.target.value})}
              placeholder="Enter admin user ID"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              placeholder="Enter full name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pin">PIN</Label>
            <Input
              id="pin"
              type="password"
              value={form.pin}
              onChange={(e) => setForm({...form, pin: e.target.value})}
              placeholder="Enter 4-digit PIN"
              maxLength={4}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPin">Confirm PIN</Label>
            <Input
              id="confirmPin"
              type="password"
              value={form.confirmPin}
              onChange={(e) => setForm({...form, confirmPin: e.target.value})}
              placeholder="Confirm PIN"
              maxLength={4}
              required
            />
          </div>

          <Button 
            type="submit" 
            className="w-full bg-red-600 hover:bg-red-700"
            disabled={loading}
          >
            {loading ? "Creating Admin..." : "Create Admin"}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}